/**
 * Client-side derived columns: formulas on values from Orion leaf calcs (same row),
 * resolved via the same calcKey + CALC_SOURCES.fields pipeline as material columns.
 *
 * Formulas are documented in DEVELOPMENT_NOTES — confirm with stakeholders before
 * treating labels (DVPI, MOIC) as authoritative business definitions.
 */

import { CALC_SOURCES, COLUMN_CATALOG_MAP } from './constants'
import { resolveColumnOptions, toNumber } from './calcResolve'

/**
 * Registry keyed by catalog `deriveId`.
 * calcInputs: semantic inputs; each row maps to one calcKey via source + optionTemplate.
 *
 * `calcInputs` may be either:
 *   - An array (static dependencies, e.g. DVPI / MOIC).
 *   - A function `(col) => array` returning inputs derived from the column's
 *     own options (e.g. mvChange, where the user picks contributing activity
 *     types). Use `resolveCalcInputs(def, col)` to get the array regardless.
 */
export const DERIVE_REGISTRY = {
  dvpi: {
    description: 'Distributions to paid-in: (cash + recallable) / contributed',
    calcInputs: [
      { key: 'contributed', source: 'aip', field: 'contributedCapital', optionTemplate: {}, representativeColumnType: 'aip:contributedCapital' },
      { key: 'cashDist', source: 'aip', field: 'cashDist', optionTemplate: {}, representativeColumnType: 'aip:cashDist' },
      { key: 'recallDist', source: 'aip', field: 'recallDist', optionTemplate: {}, representativeColumnType: 'aip:recallDist' }
    ],
    compute: (vals) => {
      const denom = toNumber(vals.contributed)
      if (denom == null || denom === 0) return null
      const cash = toNumber(vals.cashDist) ?? 0
      const recall = toNumber(vals.recallDist) ?? 0
      return (cash + recall) / denom
    }
  },
  moic: {
    description: 'Total value / paid-in (TVPI-style): (market value + cash + recallable) / contributed',
    calcInputs: [
      { key: 'marketValue', source: 'marketValue', field: 'primary', optionTemplate: {}, representativeColumnType: 'marketValue' },
      { key: 'contributed', source: 'aip', field: 'contributedCapital', optionTemplate: {}, representativeColumnType: 'aip:contributedCapital' },
      { key: 'cashDist', source: 'aip', field: 'cashDist', optionTemplate: {}, representativeColumnType: 'aip:cashDist' },
      { key: 'recallDist', source: 'aip', field: 'recallDist', optionTemplate: {}, representativeColumnType: 'aip:recallDist' }
    ],
    compute: (vals) => {
      const denom = toNumber(vals.contributed)
      if (denom == null || denom === 0) return null
      const mv = toNumber(vals.marketValue) ?? 0
      const cash = toNumber(vals.cashDist) ?? 0
      const recall = toNumber(vals.recallDist) ?? 0
      return (mv + cash + recall) / denom
    }
  },
  mvChange: {
    description: 'Market value change: EMV - (BMV + sum of contributing activity values). Contributing activity types are user-configurable; clearing all types renders the column as empty.',
    /*
     * Per-type fan-out is intentional. We could implement this as a single
     * activityType calc with all types in `details[]` (Orion sums them), but
     * splitting per-type maximizes calcKey dedup against any user-added
     * single-type Activity column (the dedup-against-user-Activity test in
     * derivedColumns.test.js exercises this contract). Tradeoff: linearly
     * more Orion calcs and request payload as the contributing-types list
     * grows (5 today by default, up to 13 if the user picks all). Repeated
     * at every grouping level when nested. If perf becomes an issue with
     * deep grouping, consider collapsing to one combined calc and dropping
     * the per-type dedup story.
     */
    calcInputs: (col) => {
      // No fallback to MV_CHANGE_DEFAULT_ACTIVITY_TYPES: if the user has
      // explicitly cleared the multiselect, return only BMV/EMV inputs.
      // compute() detects the absence of act_* keys and returns null,
      // so the column renders as `--` until contributing types are picked.
      // The catalog's settingsSchema.defaultValue still seeds new mvChange
      // columns with the default set on creation.
      const types = Array.isArray(col?.options?.activityTypes) ? col.options.activityTypes : []
      return [
        {
          key: 'bmv',
          source: 'marketValue',
          field: 'primary',
          optionTemplate: { valuationMethod: 'Beginning Market Value', includeAccruedInterest: false },
          representativeColumnType: 'marketValue'
        },
        {
          key: 'emv',
          source: 'marketValue',
          field: 'primary',
          optionTemplate: { valuationMethod: 'Ending Market Value', includeAccruedInterest: false },
          representativeColumnType: 'marketValue'
        },
        ...types.map((t, i) => ({
          key: `act_${i}`,
          source: 'activityType',
          field: 'primary',
          // Hardcoded `inceptionValueMethod: 'New Money'`. Matches the
          // default for the Activity column so a user-added single-type
          // Activity column dedups against this synthetic. If the user
          // changes their Activity column's inception method (e.g. to
          // 'Zero'), dedup fragments and both an extra synthetic and the
          // user's calc are emitted -- functionally correct, just one
          // extra Orion call. mvChange itself doesn't expose an inception
          // setting today; `dependencyOptions.activityType.inceptionValueMethod`
          // can override this per-mvChange-column if a future need appears.
          optionTemplate: { activityTypes: [t], inceptionValueMethod: 'New Money' },
          representativeColumnType: 'activity'
        }))
      ]
    },
    compute: (vals) => {
      // No act_* keys means calcInputs returned no activity inputs, which
      // only happens when the user explicitly cleared contributing types.
      // Surface as null so the cell renders `--` and the user is prompted
      // (via the panel warning) to pick contributing types.
      const hasActivityInputs = Object.keys(vals).some(k => k.startsWith('act_'))
      if (!hasActivityInputs) return null
      const emv = toNumber(vals.emv)
      if (emv == null) return null
      const bmv = toNumber(vals.bmv) ?? 0
      const activitySum = Object.keys(vals)
        .filter(k => k.startsWith('act_'))
        .reduce((s, k) => s + (toNumber(vals[k]) ?? 0), 0)
      return emv - (bmv + activitySum)
    }
  }
}

/**
 * Returns the calcInputs array for a derived column, supporting both static
 * arrays and callable forms. The callable form receives the full column
 * instance so dynamic inputs can read user-selected options.
 */
export function resolveCalcInputs (def, col) {
  if (!def) return []
  if (typeof def.calcInputs === 'function') return def.calcInputs(col) || []
  return def.calcInputs || []
}

/**
 * Pre-resolve merge: defaultOptions + registry input.optionTemplate + column.options.dependencyOptions[source].
 * Used for synthetic column `options` and must match resolveCalcAssignments + resolveColumnOptions.
 */
export function getMergedDependencyOpts (input, columnOptions) {
  const source = CALC_SOURCES[input.source]
  if (!source) return null
  return {
    ...source.defaultOptions,
    ...input.optionTemplate,
    ...(columnOptions?.dependencyOptions?.[input.source] || {})
  }
}

/** Resolved column options for calcKey identity (global date fallback applied). */
export function mergeDependencyInputOptions (input, columnOptions, globalDateConfig) {
  const mergedOpts = getMergedDependencyOpts(input, columnOptions)
  if (!mergedOpts) return null
  return resolveColumnOptions(mergedOpts, globalDateConfig)
}

function resolveInputValue (input, columnOptions, calcAssignments, leafCalcsById, globalDateConfig) {
  const source = CALC_SOURCES[input.source]
  if (!source) return null
  const resolved = mergeDependencyInputOptions(input, columnOptions, globalDateConfig)
  if (!resolved) return null
  const key = source.calcKey(resolved)
  const group = calcAssignments.get(key)
  if (!group) return null
  const calc = leafCalcsById.get(group.calcId)
  if (!calc) return null
  const fieldDef = source.fields[input.field]
  if (!fieldDef) return null
  return fieldDef.extract(calc, columnOptions)
}

/**
 * Mutates `row` to add values for derived column instance ids.
 * Reads inputs from leaf Orion calcs via assignments (same calcKey path as the request).
 */
export function applyDerivedColumns (row, columnInstances, catalogMap, calcAssignments, leafCalcs, globalDateConfig) {
  if (!row || !columnInstances?.length) return

  if (row.rowType === 'benchmark') {
    for (const col of columnInstances) {
      const catalog = catalogMap[col.columnType]
      if (catalog?.kind === 'derived') row[col.id] = null
    }
    return
  }

  const leafCalcsById = new Map(
    (leafCalcs || []).filter(c => c?.id).map(c => [c.id, c])
  )

  for (const col of columnInstances) {
    const catalog = catalogMap[col.columnType]
    if (!catalog || catalog.kind !== 'derived') continue
    const deriveId = catalog.deriveId
    const def = DERIVE_REGISTRY[deriveId]
    if (!def) continue
    const inputs = resolveCalcInputs(def, col)
    if (!inputs.length) continue

    const vals = {}
    for (const input of inputs) {
      vals[input.key] = resolveInputValue(input, col.options, calcAssignments, leafCalcsById, globalDateConfig)
    }

    row[col.id] = def.compute(vals)
  }
}

export function isDerivedColumnType (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'derived'
}
